import React from 'react';

import { type Shown, formatExact, formatValue, memberLabel, shortDay } from '../format';
import type { Block, Envelope, EnvelopeColumn, EnvelopeRow } from '../types';

type BulletProps = { block: Block; envelope: Envelope; shown: Shown };

const ratio = (part: number | null, whole: number | null) => (part === null || whole === null || whole === 0 ? null : part / whole);

export default function Bullet({ block, envelope, shown }: BulletProps) {
  const measures = envelope.columns.filter((column) => column.role === 'measure');
  const dims = envelope.columns.filter((column) => column.role === 'dim');
  const find = (ref: unknown) => (typeof ref === 'string' ? measures.find((column) => column.name === ref || column.name.endsWith(`.${ref}`) || ref.endsWith(`.${column.name}`)) : undefined);
  const actual = find(block.kpi) ?? measures[0];
  const forecast = find(block.forecast);
  const target = find(block.target);
  const fixedTarget = typeof block.target === 'number' ? block.target : null;
  const rows: Array<{ key: string; label: string; row: EnvelopeRow }> = dims.length
    ? envelope.rows.filter((row) => !row.other).map((row) => ({ key: dims.map((dim) => String(row[dim.name] ?? '')).join('›'), label: dims.map((dim) => String(row[dim.name] ?? '')).map((member) => (/^\d{4}-\d{2}-\d{2}$/.test(member) ? shortDay(member) : memberLabel(member))).join(' · '), row }))
    : envelope.rows.slice(0, 1).map((row) => ({ key: 'total', label: actual.label, row }));
  const value = (row: EnvelopeRow, column?: EnvelopeColumn) => (column ? ((row[column.name] as number | null | undefined) ?? null) : null);
  const targetOf = (row: EnvelopeRow) => (target ? value(row, target) : fixedTarget);
  const largest = Math.max(...rows.flatMap(({ row }) => [value(row, actual), value(row, forecast), targetOf(row)].map((entry) => Math.abs(entry ?? 0))), actual.format === 'pct' ? 1 : 0) || 1;
  const at = (entry: number | null) => `${Math.max(0, Math.min(100, ((entry ?? 0) / largest) * 100))}%`;
  const show = (entry: number | null, column: EnvelopeColumn) => formatValue(entry, column.format, { numbers: shown.numbers, decimals: column.decimals });
  return (
    <div className={`bul${dims.length ? ' many' : ''}`}>
      {rows.map(({ key, label, row }) => {
        const now = value(row, actual);
        const ahead = value(row, forecast);
        const goal = targetOf(row);
        const reach = ratio(ahead ?? now, goal);
        return (
          <div className="bul-row" key={key}>
            {dims.length ? <span className="bul-l" title={label}>{label}</span> : null}
            <span className="bul-track">
              {forecast ? <i className="bul-f" style={{ width: at(ahead) }} /> : null}
              <i className="bul-a" style={{ width: at(now) }} />
              {goal !== null ? <i className="bul-t" style={{ left: at(goal) }} /> : null}
            </span>
            <span className="bul-v">
              <b data-lens-ref={`${envelope.query}.${actual.name}[${key}]`} title={formatExact(now, actual.format)}>
                {show(now, actual)}
              </b>
              {forecast ? (
                <span className="bul-n" data-lens-ref={`${envelope.query}.${forecast.name}[${key}]`} title={`${forecast.label} ${formatExact(ahead, forecast.format)}`}>
                  {` → ${show(ahead, forecast)}`}
                </span>
              ) : null}
              {goal !== null && reach !== null ? (
                <span className={`bul-n ${reach < 1 ? 'down' : 'up'}`} data-lens-ref={target ? `${envelope.query}.${target.name}[${key}]` : undefined} title={target ? `${target.label} ${formatExact(goal, target.format)}` : undefined}>
                  {` · ${Math.round(reach * 100)}% of ${target?.label.toLowerCase() ?? 'target'}`}
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}
